import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';

import FormControl from '../FormControl';
import { Close } from '../../Icons';

export interface InputTextProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  placeholder?: string;
  inputClassName?: string;
  size?: 'sm' | 'md' | 'lg';
  isError?: boolean;
  helperText?: React.ReactNode | string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onClear?: () => void;
}

const InputText = React.forwardRef<HTMLInputElement, InputTextProps>(
  (
    {
      className,
      inputClassName,
      label,
      placeholder,
      required,
      disabled,
      size,
      leftIcon,
      rightIcon,
      type,
      helperText,
      onClear,
      isError,
      onChange,
      value,
      defaultValue,
      ...rest
    },
    ref
  ) => {
    const [currentValue, setCurrentValue] = useState<
      string | ReadonlyArray<string> | number
    >(value ?? defaultValue);

    const resetValue = useCallback(() => {
      setCurrentValue('');
      onClear && onClear();
    }, []);

    const isEmpty = useMemo(() => !currentValue, [currentValue]);

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange && onChange(event);
      setCurrentValue(event.target.value);
    };

    useEffect(() => {
      setCurrentValue(value ?? defaultValue ?? '');

      return () => setCurrentValue('');
    }, [value, defaultValue]);

    return (
      <FormControl
        required={required}
        disabled={disabled}
        error={isError}
        size={size}
        className={className}
      >
        <div
          className={cx('flex items-center px-2 gap-2', {
            'h-[40px]': size === 'sm',
            'h-[50px]': size === 'md',
            'h-[60px]': size === 'lg',
          })}
        >
          {leftIcon && leftIcon}

          <div className="relative flex flex-col grow justify-center h-full">
            {label && <FormControl.Label>{label}</FormControl.Label>}

            <input
              {...rest}
              ref={ref}
              type={type}
              required={required}
              disabled={disabled}
              placeholder={placeholder}
              className={cx(
                'input-text',
                size,
                inputClassName && inputClassName
              )}
              onChange={handleOnChange}
              value={currentValue ?? ''}
              defaultValue={defaultValue}
            />

            {onClear && !isEmpty && (
              <Close
                className="absolute right-0 fill-gray-500 w-5 h-5 cursor-pointer hover:opacity-75"
                onClick={resetValue}
              />
            )}
          </div>

          {rightIcon && rightIcon}
        </div>

        {helperText && (
          <FormControl.HelperText>{helperText}</FormControl.HelperText>
        )}
      </FormControl>
    );
  }
);

InputText.displayName = 'InputText';
InputText.defaultProps = {
  size: 'sm',
  required: false,
  type: 'text',
  isError: false,
};

export default InputText;
